import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { searchUsers } from "@/services/firebase/user";
import { User } from "@/types/user";
import UserAvatar from "@/components/common/UserAvatar";
import ActionMenu from "@/components/common/ActionMenu";
import { usePermissions } from "@/hooks/usePermissions";
import { AdminPage } from "@/components/admin-ui/admin-page";
import { AdminPageHeader } from "@/components/admin-ui/admin-page-header";
import { AdminSearchInput } from "@/components/admin-ui/admin-search-input";
import { AdminDataTable, AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { AdminLoadingSkeleton } from "@/components/admin-ui/admin-loading-skeleton";
import { AdminErrorAlert } from "@/components/admin-ui/admin-error-alert";
import { AdminEmptyState } from "@/components/admin-ui/admin-empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const columns: AdminTableColumn[] = [
  { key: "user", label: "使用者" },
  { key: "membership", label: "會員狀態", hideBelow: "sm" },
  { key: "coin", label: "金幣", hideBelow: "md", sortable: true },
  { key: "createdAt", label: "註冊時間", hideBelow: "md", sortable: true },
  { key: "actions", label: "操作" },
];

export default function UserListPage() {
  const navigate = useNavigate();
  const { canManageUsers, isStaff } = usePermissions();
  const [searchQuery, setSearchQuery] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const {
    data: users,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["users", "search", searchQuery],
    queryFn: () => searchUsers(searchQuery),
    enabled: false, // 不自動執行，需要手動觸發
  });

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      return;
    }
    console.log("[UserListPage] 開始搜尋:", searchQuery);
    setHasSearched(true);
    try {
      const result = await refetch();
      console.log("[UserListPage] 搜尋結果:", result.data?.length || 0, "個使用者");
      if (result.error) {
        console.error("[UserListPage] 搜尋錯誤:", result.error);
      }
    } catch (err) {
      console.error("[UserListPage] 搜尋異常:", err);
    }
  };

  const handleViewDetail = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  // 檢查權限
  if (!canManageUsers) {
    return (
      <AdminPage>
        <AdminErrorAlert
          message={`您沒有權限訪問此頁面。只有管理員和工作人員可以管理使用者。（您的權限狀態：${isStaff ? "工作人員" : "一般用戶"}）`}
        />
      </AdminPage>
    );
  }

  const renderCell = (user: User, column: AdminTableColumn) => {
    switch (column.key) {
      case "user":
        return (
          <div className="flex items-center gap-3">
            <UserAvatar user={user} size="md" />
            <div>
              <div className="text-sm font-medium text-foreground">
                {user.displayName || "未設定名稱"}
              </div>
              <div className="text-xs text-muted-foreground">
                {user.email || user.id}
              </div>
            </div>
          </div>
        );
      case "membership":
        return user.is_member ? (
          <Badge variant="purple">
            {user.member_type === "yearly" ? "年會員" : "週會員"}
          </Badge>
        ) : (
          <span className="text-sm text-muted-foreground">一般用戶</span>
        );
      case "coin":
        return (
          <span className="text-sm text-muted-foreground">
            {user.coin?.toLocaleString() || 0}
          </span>
        );
      case "createdAt":
        return (
          <span className="text-sm text-muted-foreground">
            {user.created_at || user.createdAt
              ? new Date(user.created_at || user.createdAt!).toLocaleDateString("zh-TW")
              : "-"}
          </span>
        );
      case "actions":
        return (
          <ActionMenu
            actions={[
              {
                label: "查看詳情",
                onClick: () => handleViewDetail(user.id),
              },
            ]}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AdminPage>
      <AdminPageHeader
        title="使用者管理"
        subtitle={`請輸入 email、姓名或使用者 ID 進行搜尋${isStaff ? " — 工作人員權限" : ""}`}
      />

      {/* 搜尋框 */}
      <form onSubmit={handleSearch} className="mb-6 flex items-center gap-4">
        <AdminSearchInput
          value={searchQuery}
          onChange={(val) => {
            setSearchQuery(val);
            setHasSearched(false);
          }}
          placeholder="搜尋使用者（email、姓名或使用者 ID）"
          className="max-w-md"
        />
        <Button
          type="submit"
          disabled={isLoading || !searchQuery.trim()}
        >
          {isLoading ? "搜尋中..." : "搜尋"}
        </Button>
      </form>

      <p className="mb-6 text-xs text-muted-foreground">
        為保護使用者隱私，請輸入搜尋條件後才會顯示結果
      </p>

      {/* 錯誤狀態 */}
      {error && (
        <AdminErrorAlert
          message={`搜尋使用者時發生錯誤：${error instanceof Error ? error.message : "未知錯誤"}`}
        />
      )}

      {/* 載入狀態 */}
      {isLoading && <AdminLoadingSkeleton rows={5} />}

      {/* 搜尋結果 */}
      {!isLoading && !error && hasSearched && (
        <>
          {users && users.length > 0 ? (
            <>
              <p className="mb-4 text-sm text-muted-foreground">
                找到 {users.length} 位使用者
              </p>
              <AdminDataTable<User>
                columns={columns}
                data={users}
                renderCell={renderCell}
                onRowClick={(user) => handleViewDetail(user.id)}
                getRowId={(user) => user.id}
              />
            </>
          ) : (
            <AdminEmptyState
              icon={<Search className="h-5 w-5" />}
              title="未找到符合條件的使用者"
            />
          )}
        </>
      )}

      {/* 初始狀態提示 */}
      {!isLoading && !error && !hasSearched && (
        <AdminEmptyState
          icon={<Search className="h-5 w-5" />}
          title="請在上方輸入搜尋條件"
          description="為保護使用者隱私，需要搜尋後才會顯示結果"
        />
      )}
    </AdminPage>
  );
}
